import React, { FunctionComponent, ReactNode } from 'react'

import { LinearGradient } from 'expo-linear-gradient'
import styled, { useTheme } from 'styled-components/native'

import { IS_WEB } from '../../constants'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export interface AuroraAwareBackgroundProps {
  children: ReactNode
  fallbackBackground?: {
    component: React.ElementType
    props?: Record<string, any>
  }
  /** When true, the gradient is contained within its parent (position: absolute) rather than fixed to the viewport. */
  contained?: boolean
  /** When true, hides the gradient background while keeping the wrapper structure. */
  hideGradient?: boolean
}

const GradientContainer = styled.View({
  flexGrow: 1,
  position: 'relative',
})

const FixedGradient = styled(LinearGradient)<{ contained?: boolean }>(({ contained }) => ({
  top: 0,
  left: 0,
  right: 0,
  ...(IS_WEB && !contained
    ? {
        position: 'fixed',
        height: '100vh',
      }
    : {
        position: 'absolute',
        bottom: 0,
      }),
}))

const ContentWrapper = styled.View({
  flexGrow: 1,
})

/**
 * A wrapper component that applies an aurora-themed background gradient when the aurora theme is active,
 * and falls back to a provided background component or no background when not.
 *
 * On web, the gradient is fixed to the viewport (position: fixed) and remains static as you scroll.
 * On mobile, the gradient stretches with the content (position: absolute).
 */
export const AuroraAwareBackground: FunctionComponent<AuroraAwareBackgroundProps> = ({
  children,
  fallbackBackground,
  contained,
  hideGradient,
}) => {
  const {
    colors: { backgroundGradientFirst, backgroundGradientSecond },
    isAuroraTheme,
  } = useTheme() as ThemeType

  if (!isAuroraTheme) {
    if (fallbackBackground) {
      const { component: FallbackBackgroundComponent, props } = fallbackBackground
      return <FallbackBackgroundComponent {...props}>{children}</FallbackBackgroundComponent>
    }
    return <>{children}</>
  }

  const gradientColors = [backgroundGradientFirst, backgroundGradientSecond] as [string, string]

  return (
    <GradientContainer testID={tID('AuroraGradientBackground')}>
      {!hideGradient && (
        <FixedGradient
          contained={contained}
          colors={gradientColors}
          start={{ x: 0.5, y: 0 }}
          end={{ x: 0.5, y: 1 }}
          locations={[0, 1]}
        />
      )}
      <ContentWrapper>{children}</ContentWrapper>
    </GradientContainer>
  )
}
